import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Input, OnDestroy, OnInit } from "@angular/core";
import { TradeComponent } from "../trade.interface";
import { FiatService } from "@main-services/currency/fiat.service";
import { UserService } from "@main-services/user.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { ModalHeader } from "@models/modal_step";
import { TradeOfferService } from "../../services/trade-offer.service";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { TradeOfferStatus } from "@models/trade-offer.model";
import { AccountService } from "../../../account/services/account.service";
import { TradePendingComponent } from "../../../main/components/step-summary/success-screens/trade-pending/trade-pending.component";
import { ApicallService } from "@core-services/apicall.service";
import { InfoService } from "@core-services/info.service";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { BehaviorSubject, EMPTY, Observable, of } from "rxjs";
import { Feature } from "@models/feature-list";
import { StockTestService } from "@main-services/stock-test.service";
import { StockTestHelperService } from "@main-services/stock-test-helper.service";
import { Tradable } from "@models/interfaces/tradable";
import { StakedTradeFacade } from "@modules/staking/domain/+state/staked-trade.facade";
import { LeveragedTokensFacade } from "@modules/main/components/leveraged-tokens/state/leveraged-tokens.facade";
import { ICashPlusFiatWallet, SellCombinedFiatWallet } from "@modules/cashplus/entities/cashplus.model";
import { FiatWallet } from "@models";
import { CashplusTradingFacade } from "@modules/trade/services/cashplus-trading-facade.service";
import { TaxesFacade } from "@modules/taxes/domain/+state/taxes.facade";
import {
	CRYPTO_TYPE,
	INDEX_TYPE,
	LIMIT_ORDERS_SWITCH_VIEWED,
	TAXES_NOT_ELIGIBLE_COOKIE_KEY,
	TRADABLES_TYPE
} from "@helpers/constants";
import { TaxesQueryParamName, TaxesQueryParamsCollection } from "@modules/taxes/domain/entities/taxes-navigation.model";
import {
	ITaxInOffer,
	SelfCertificationModel,
	TaxesRouteNames,
	TaxesUserStatusModel,
	ToastMessages
} from "@modules/taxes/domain/entities/taxes.model";
import { IBCITaxCreateRequest } from "@modules/taxes/domain/infrastructure/taxes.remote";
import { BciTradeAction } from "@gql/offer";
import { ToastrService } from "ngx-toastr";
import { Helper } from "@helpers/functions";
import { catchError, filter, switchMap, take, tap } from "rxjs/operators";
import { OrdersEntryService } from "@modules/limit-orders/services/orders-entry.service";
import { LimitOrderQueryParamName } from "@modules/limit-orders/interfaces/limit-orders-route.interface";
import {
	IOrderSwitchVisibilityType,
	ISwitchSeenCookie
} from "@modules/limit-orders/interfaces/limit-order.view.interface";

@Component({
	templateUrl: "./trade-sell.component.html",
	styleUrls: ["../trade-modal.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [CashplusTradingFacade],
	standalone: false
})
export class TradeSellComponent extends TradeComponent implements OnInit, OnDestroy {
	private tradeFacade = inject(CashplusTradingFacade);
	protected taxesFacade = inject(TaxesFacade);
	protected cdr = inject(ChangeDetectorRef);
	private readonly toastr = inject(ToastrService);
	protected label: string = "sell";
	public subtitle: string = "";

	public featureList = Feature;

	protected readonly flowName: string = "Sell";
	protected readonly pathPrepend: string = "/trade/sell";

	public isAssetStaked$: Observable<boolean> = this.stakedTradeFacade.isStakedAsset$();
	public cashPlusFiatTilesVM$: Observable<SellCombinedFiatWallet>;

	public wallets: TradableWallet[] = [];
	public assetType = TRADABLES_TYPE;

	//Tax Withheld
	public taxWithheld$: Observable<ITaxInOffer | null>;
	// public twSummaryVariationFF$ = this.taxesFacade.isTaxesTradeSummaryVariationActive$;
	private taxWithheldBS$ = new BehaviorSubject<ITaxInOffer | null>(null);

	private readonly ordersEntryService = inject(OrdersEntryService);

	@Input()
	public fromTWFlow: boolean = false;
	public isTaxesLoading: boolean = false;

	private sellLimitOrderEnabled: boolean = false;

	public constructor(
		protected walletService: TradableWalletListService,
		protected fiatService: FiatService,
		protected tradableListService: TradableListService,
		protected fiatwalletService: FiatWalletService,
		protected apicallService: ApicallService,
		protected accountService: AccountService,
		protected infoService: InfoService,
		protected userService: UserService,
		protected tradeOfferService: TradeOfferService,
		protected launchDarklyService: LaunchDarklyService,
		protected confirmModalService: ConfirmModalService,
		protected stockTestService: StockTestService,
		protected stockTestHelperService: StockTestHelperService,
		private stakedTradeFacade: StakedTradeFacade,
		protected leveragedTokenFacade: LeveragedTokensFacade
	) {
		super(
			apicallService,
			fiatwalletService,
			infoService,
			tradableListService,
			fiatService,
			accountService,
			walletService,
			userService,
			tradeOfferService,
			launchDarklyService,
			confirmModalService,
			leveragedTokenFacade
		);
	}

	public ngOnInit(): void {
		this.cashPlusFiatTilesVM$ = this.tradeFacade.sellFiatTilesVM$(this.getWalletForCashPlus());
		super.ngOnInit();

		this.launchDarklyService
			.isActive$(LD_FLAG.LIMIT_ORDERS_SELL)
			.pipe(take(1))
			.subscribe((isSellLimitOrderEnabled) => {
				this.sellLimitOrderEnabled = isSellLimitOrderEnabled;
			});

		// If tradableId is set, sell flow is coming from another entry point
		if (this.tradableId) {
			this.stakedTradeFacade.fetchStakeableDataById$(this.tradableId);
			const currencyType = this.tradableListService.getItemById(this.data.getCurrencyId()).getCurrencyType();
			this.taxesFacade.isTaxesForTradeActive$
				.pipe(
					switchMap((isActive) => {
						if (isActive && (currencyType === CRYPTO_TYPE || currencyType === INDEX_TYPE)) {
							this.isTaxesLoading = true;
							return this.taxesFacade
								.fetchTaxStatusForTrade$(this.data?.getWalletId())
								.pipe(catchError((_) => of("error")));
						}

						return EMPTY;
					})
				)
				.subscribe({
					next: (tax) => {
						if (tax === "error" && Helper.getCookie(TAXES_NOT_ELIGIBLE_COOKIE_KEY, true) !== "true") {
							this.close();
							this.toastr.error(this.translationService.translate(ToastMessages.Error));
							this.isTaxesLoading = false;
							this.cdr.markForCheck();
							return;
						}
						this.isTaxesLoading = false;
						this.cdr.markForCheck();

						if (this.data.getWalletId() !== "") {
							// eslint-disable-next-line no-self-assign
							this.wallet = this.wallet;
							this.preTransBalance = this.wallet.getBalance();
						}

						const queryParams: Partial<TaxesQueryParamsCollection> = {
							assetId: this.data.getCurrencyId(),
							assetWalletId: this.data.getWalletId(),
							fromTradeFlow: this.isLimitOrder
								? TaxesRouteNames.SellOrderTrade
								: TaxesRouteNames.SellTrade
						};

						if (!this.fromTWFlow) {
							this.taxesFacade.openCryptoDeclarationTradeModal(queryParams, tax as TaxesUserStatusModel);
						}
					}
				});
		}

		//Tax Withheld for summary
		this.taxWithheld$ = this.taxesFacade.isTaxesForTradeActive$.pipe(
			tap((active) => {
				if (active) {
					//this is a hack to trigger first value of taxWithheldBS$ observable
					this.taxWithheldBS$.next(this.data.getTax());
				}
			}),
			switchMap((active) => {
				if (active) {
					if (this.isIndexTrade()) {
						const request: IBCITaxCreateRequest = {
							amount: this.data.getAmountFiat(),
							assetId: this.data.getCurrencyId(),
							fiatId: this.data.getFiatId(),
							type: BciTradeAction.Sell
						};

						return this.taxesFacade.fetchBCITaxCreate$(request);
					}
					// return non index tax
					return this.taxWithheldBS$.asObservable();
				}
				return of(null);
			})
		);
	}

	public refreshTaxWithheld(updated: ITaxInOffer | null): void {
		if (updated) {
			this.taxWithheldBS$.next(updated);
		}
	}

	public selectCashPlusFiatWallet(wallet: ICashPlusFiatWallet): void {
		this.selectFiatWallet(wallet.fiatWallet);
		if (wallet.isEligible) {
			this.selectedCashPlusWallet = wallet;
		}
	}

	public selectFiatWallet(w: FiatWallet): void {
		if (this.isLimitOrder) {
			this.ordersEntryService.openSellOrderFlow(this.wallet.getCurrencyId(), {
				[LimitOrderQueryParamName.WalletId]: this.wallet.getId(),
				[LimitOrderQueryParamName.FiatId]: w.getFiatId(),
				[LimitOrderQueryParamName.FiatWalletId]: w.getId()
			});
			this.close();
		}
		super.selectFiatWallet(w);
	}

	public ngOnDestroy(): void {
		super.ngOnDestroy();
		this.stakedTradeFacade.removeStakeable();
	}

	public selectWallet(wallet: TradableWallet, cashPlusWallets?: ICashPlusFiatWallet[]): void {
		const currencyType = this.tradableListService.getItemById(wallet.getCurrencyId()).getCurrencyType();

		this.taxesFacade.isTaxesForTradeActive$
			.pipe(
				switchMap((isActive) => {
					if (isActive && (currencyType === CRYPTO_TYPE || currencyType === INDEX_TYPE)) {
						return this.taxesFacade
							.fetchTaxStatusForTrade$(wallet.getId())
							.pipe(catchError((_) => of("error")));
					}

					super.selectWallet(wallet, cashPlusWallets);
					return EMPTY;
				})
			)
			.subscribe({
				next: (tax) => {
					if (tax === "error" && Helper.getCookie(TAXES_NOT_ELIGIBLE_COOKIE_KEY, true) !== "true") {
						this.toastr.error(this.translationService.translate(ToastMessages.Error));
						return;
					}

					const queryParams: Partial<TaxesQueryParamsCollection> = {
						[TaxesQueryParamName.AssetId]: wallet.getCurrencyId(),
						[TaxesQueryParamName.AssetWalletId]: wallet.getId(),
						[TaxesQueryParamName.FromTradeFlow]: this.isLimitOrder
							? TaxesRouteNames.SellOrderTrade
							: TaxesRouteNames.SellTrade
					};

					this.taxesFacade.openCryptoDeclarationTradeModal(queryParams, tax as TaxesUserStatusModel);

					const { taxStatus, openDeclarationsCount } = tax as TaxesUserStatusModel;

					if (
						taxStatus === SelfCertificationModel.NonEligible ||
						Helper.getCookie(TAXES_NOT_ELIGIBLE_COOKIE_KEY, true) === "true" ||
						(openDeclarationsCount === 0 && taxStatus === SelfCertificationModel.Eligible)
					) {
						super.selectWallet(wallet, cashPlusWallets);
						this.cdr.markForCheck();
					}
				}
			});
	}

	private getWalletForCashPlus(): FiatWallet[] {
		return this.fiatwalletService.list;
	}

	// new onClick method that does the logic previously contained within tradable-to-list component
	public onTradableSelect(tradable: Tradable): void {
		const wallets = this.walletService
			.getWalletsByTradableId(tradable.getId())
			.filter((wallet) => !this.isLessThanMinSell(wallet, tradable));

		if (wallets.length === 1) {
			// if 1 wallet, set it to null to not show multiple wallets screen
			this.tradableId = null;
			this.onWalletSelect(wallets[0]);
		} else {
			// if multiple wallets, go to wallet selection screen
			this.wallets = wallets;
			this.tradableId = tradable.getId();
		}

		this.stakedTradeFacade.fetchStakeableDataById$(tradable.getId());
	}

	private isLessThanMinSell(wallet: TradableWallet, tradable: Tradable): boolean {
		return wallet.getBalance() * tradable.getPrice() < tradable.getMinSell();
	}

	public onWalletSelect(wallet: TradableWallet): void {
		const callback: Function = () => this.selectWallet(wallet);

		if (this.stockTestService.needsTest(wallet.getCurrencyId())) {
			this.stockTestHelperService.startTest(callback);
		} else {
			callback();
		}
	}

	public showSuccessScreen(tradeStatus: TradeOfferStatus): void {
		this.done();
		if (tradeStatus === "SUCCESS") {
			this.showTradeSuccessScreen({
				offer: this.data,
				itbSuccess: this.getItbTradeSuccess(),
				previousBalance: this.preTransBalance
			});
		} else {
			const walletExists = !this.wallet.isNew();
			this.modalService
				.create(TradePendingComponent, {
					offer: this.data,
					walletExists,
					itbSuccess: this.getItbTradeSuccess(),
					hasItb: this.hasItb(),
					previousBalance: this.preTransBalance
				})
				.present();
		}
	}

	public get currentStep(): ModalHeader {
		const header: ModalHeader = this.getDefaultHeader(this.getTitle());

		if (
			(this.isFirstStep && this.tradableId) ||
			(this.isSecondStep && this.isEdit(0) && !this.data.getDirectTradableWalletOffer()) ||
			(this.isThirdStep && !this.isEdit(3))
		) {
			header.left = "back";
			this.subtitle = this.getSubtitle();
		} else if (this.isFirstStep && this.isEdit(1)) {
			header.left = "abort";
		} else if (this.isSecondStep && this.isEdit(2)) {
			header.left = "cancel";
		}
		if ((this.isFirstStep && this.isEdit(1)) || (this.isSecondStep && this.isEdit(2))) {
			header.right = "ok";
		} else if ((this.isFirstStep && !this.isEdit(1)) || this.isThirdStep) {
			header.right = "abort";
		} else if (this.isSecondStep && !this.isEdit(2)) {
			header.right = "cancel";
		}
		return header;
	}

	public onHeaderButtonClick(name: string): void {
		// Can probably be unified for all flows in the future
		if (name === "back") {
			if (this.isFirstStep) {
				this.resetTradable();
			}
			if (this.isSecondStep) {
				this.tradableId = null;
			}
			if (this.isThirdStep) {
				this.priceSubject$.next(null);
				//navigate away and update switch visibility cookie from amount select
				this.navigateAwayAndUpdateSwitchVisilibityCookie();
			}
			this.stepBack();
		} else {
			super.onHeaderButtonClick(name);
		}
	}

	public getTitle(): string {
		switch (this.stepNumber) {
			case 1:
				return this.translate("sell_cryptocoins");
			case 2:
				return this.translate("trade_fiat_select_header");
			case 3:
				if (this.sellLimitOrderEnabled) {
					return this.translate("sell_market_order");
				}
				return this.translate("trade_amount_select_header");
			default:
				return "";
		}
	}

	private getSubtitle(): string {
		if (this.getTradable().isToken()) {
			return "fma_select_amount";
		}
		return "sell_amount_of_crypto";
	}

	public modalCanDismiss(): boolean {
		return true;
	}

	//cookie helper for switch visibility
	private navigateAwayAndUpdateSwitchVisilibityCookie(): void {
		this.launchDarklyService
			.getFeatureConfig$(LD_FLAG.LIMIT_ORDERS_SWITCH_VARIATIONS)
			.pipe(
				take(1),
				filter((config) => !!config)
			)
			.subscribe((config) => {
				const asset = this.tradableListService.getItemById(this.data.getCurrencyId());
				const isAssetAvailableForLimitOrder = asset.getAssetSettings().isPriceOrderAvailable();

				if (!isAssetAvailableForLimitOrder) {
					return;
				}

				if (config === IOrderSwitchVisibilityType.Show_Once) {
					Helper.setCookie(LIMIT_ORDERS_SWITCH_VIEWED, ISwitchSeenCookie.Seen, true);
				}

				if (config === IOrderSwitchVisibilityType.Show_Always) {
					Helper.setCookie(LIMIT_ORDERS_SWITCH_VIEWED, ISwitchSeenCookie.NotSeen, true);
				}
			});
	}

	public getOffer(): void {
		super.getOffer();
		this.navigateAwayAndUpdateSwitchVisilibityCookie();
	}
}
